import '../eiq-connect-account.scss';
import React, { useEffect, useRef, useState } from 'react';
import { Button, QRCode, Select, SelectProps } from 'antd';
import { formatAddressLabel } from 'common/helpers/address.helper';
import eiqConnectStore from 'features/eiq-connect/store/eiq-connect.store';
import { observer } from 'mobx-react-lite';
import { ReloadOutlined } from '@ant-design/icons';
import { notification } from 'common/utils/notification';
import { formatVehicleLabel } from 'common/helpers/vehicle.helper';
import { formatEiqPassportCode } from 'common/helpers/eiq-passport.helper';
import CountdownTimer from '../../../../../common/components/сountdown-timer/сountdown-timer';
import { add, isNumber } from 'lodash';
import dayjs from 'dayjs';
import featureStore from 'common/stores/features/feature.store';
import { ICreateResidentFastAccessPass, IResidentFastAccessPassRead } from 'common/services/fast-access-service/fast-access-service.types';
import fastAccessService from 'common/services/fast-access-service/fast-access-service';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import { trackPromise } from 'react-promise-tracker';
import configurationStore from 'common/stores/configuration/configuration.store';

const NotInOwnVehicle = 'NotInOwnVehicle';

interface IVehicleOption {
  value: number | string;
  label: string;
}

interface Props {
  selectedAddressId: number | null;
  setSelectedAddressId: React.Dispatch<React.SetStateAction<number | null>>;
}

export const AccountFastAccessBlock = observer(({ selectedAddressId, setSelectedAddressId }: Props) => {
  const [vehicleId, setVehicleId] = useState<number | null | string>(null);
  const [fastAccessPass, setFastAccessPass] = useState<IResidentFastAccessPassRead | null>(null);
  const [addressOptions, setAddressOptions] = useState<SelectProps['options']>([]);
  const [vehicleOptions, setVehicleOptions] = useState<SelectProps['options']>([]);
  const eiqPassports = useRef<IResidentFastAccessPassRead[]>([]);

  const [hideRefresh, setHideRefresh] = useState(false);

  const getEiqPassports = async () => {
    const result = await trackPromise(fastAccessService.getResidentFastAccessPasses(userAuthStore.webGuestResidentId!));
    eiqPassports.current = result;
  };

  useEffect(() => {
    if (!featureStore.isBasIPEnabled || !fastAccessPass) {
      return;
    }

    const validTo = dayjs(fastAccessPass.expires);
    const now = dayjs();

    if (validTo.diff(now, 'day') > 30) {
      setHideRefresh(true);
    } else {
      setHideRefresh(false);
    }
  }, [fastAccessPass]);

  useEffect(() => {
    const addressOptions = eiqConnectStore.residentDetails?.addresses?.map((item) => ({
      value: item.address.id,
      label: formatAddressLabel(item.address),
    }));

    setAddressOptions(addressOptions || []);

    if (addressOptions && addressOptions.length > 0 && !selectedAddressId) {
      setSelectedAddressId(addressOptions[0].value);
    }
  }, [eiqConnectStore?.residentDetails]);

  const getEiqPassport = async (addressId: number, vehicleId: number | null) => {
    await getEiqPassports();
    const result = eiqPassports.current.find((i) => i.addressId === addressId && i.vehicleId === vehicleId);

    if (result) {
      return result;
    }

    const data: ICreateResidentFastAccessPass = {
      addressId: addressId,
      residentId: userAuthStore.webGuestResidentId!,
      vehicleId: vehicleId,
    };

    return await trackPromise(fastAccessService.createResidentFastAccessPass(data));
  };

  useEffect(() => {
    if (selectedAddressId) {
      const vehicles = eiqConnectStore.residentDetails!.addresses.find((i) => i.address.id === selectedAddressId)!.vehicles;

      const vehicleOptions: IVehicleOption[] = vehicles
        .filter((i) => {
          if (!i.validTo) {
            return true;
          }

          const expirationDate = dayjs(i.validTo, 'YYYY-MM-DD').startOf('day');
          const today = dayjs().startOf('day');

          return !expirationDate.isBefore(today);
        })
        .filter((i) => !i.isDeactivated)
        .map((item) => ({
          value: item.id,
          label: formatVehicleLabel(item),
        }));

      vehicleOptions.push({
        value: NotInOwnVehicle,
        label: 'Not in own vehicle',
      });

      setVehicleOptions(vehicleOptions);
      const firstVehicleId = getVehicleId(vehicleOptions[0].value);

      getEiqPassport(selectedAddressId, getVehicleId(vehicleOptions[0].value)).then((response) => {
        setFastAccessPass(response);
      });
      setVehicleId(firstVehicleId);
    }
  }, [selectedAddressId]);

  const refreshButtonClickHandle = async () => {
    if (!selectedAddressId) {
      return;
    }

    const data: ICreateResidentFastAccessPass = {
      addressId: selectedAddressId,
      residentId: userAuthStore.webGuestResidentId!,
      vehicleId: getVehicleId(vehicleId),
    };

    const response = await trackPromise(fastAccessService.createResidentFastAccessPass(data));

    setFastAccessPass(response);
    notification.success({ message: 'QR code successfully updated' });
  };

  const getVehicleId = (vehicleId: any) => (isNumber(vehicleId) ? Number(vehicleId) : null);

  const onVehicleSelected = async (value: any) => {
    setVehicleId(value);
    const response = await getEiqPassport(selectedAddressId!, getVehicleId(value));
    setFastAccessPass(response);
  };

  return (
    <div className="eiq-account-qr-code-wrapper">
      <div className="qr-code-content">
        <div className="qr-code-body">
          <div className="qr-code-text">
            <span className="bold-text">Your personal QR code is below.</span>
            <span>
              {configurationStore.configuration?.default.eiqPassportInstractionText ??
                'Please present it when using the guest lane or kiosk, where available.'}
            </span>
          </div>
          {fastAccessPass && (
            <div className="running-border">
              <QRCode value={formatEiqPassportCode(fastAccessPass.id)} size={300} bordered={false} errorLevel="H" />
            </div>
          )}
          {!hideRefresh && <CountdownTimer expires={fastAccessPass?.expires!} expiredMessage={'QR code has expired'} />}
        </div>
        <div className="qr-code-form">
          <Select
            options={addressOptions}
            placeholder="Select address"
            className="qr-code-address-select"
            value={selectedAddressId}
            onChange={setSelectedAddressId}
          />
          <Select
            options={vehicleOptions}
            placeholder="Select vehicle"
            className="qr-code-address-select"
            value={vehicleId}
            onChange={onVehicleSelected}
          />
          {!hideRefresh && (
            <Button type="primary" icon={<ReloadOutlined />} className="full-space" onClick={refreshButtonClickHandle}>
              Refresh QR code
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});
